import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

export interface OrganizationMonitoringData {
  id: string;
  name: string;
  logo_url: string | null;
  created_at: string | null;
  website: string | null;
  subscription_status: string | null;
  active_agents_count: number | null;
  ai_assistant_instances_count?: number | null;
  ai_outputs_count?: number | null;
  is_test_org?: boolean | null;
  onboarding_meta: any | null;
  // Profile data (user associated with organization)
  profile_full_name: string | null;
  profile_email: string | null;
  profile_first_name: string | null;
  profile_last_name: string | null;
  // Industry data
  industry_name: string | null;
  industry_emoji: string | null;
  // Plan data
  plan_name: string | null;
  plan_monthly_price: number | null;
}

export const organizationMonitoringService = {
  async getRecentOrganizations(): Promise<{
    data: OrganizationMonitoringData[] | null;
    error: Error | null;
  }> {
    try {
      // Use SECURITY DEFINER function to bypass RLS and get all profile data
      const { data: organizations, error } = await supabase.rpc(
        "get_organizations_for_monitoring",
        {
          min_created_at: "2025-06-16",
          result_limit: 100,
        },
      );

      if (error) {
        console.error("Database error:", error);
        return {
          data: null,
          error: new Error(`Database error: ${error.message}`),
        };
      }

      if (!organizations || organizations.length === 0) {
        return { data: [], error: null };
      }

      // In parallel, fetch counts from aggregated views (optimized, server-side grouping)
      const orgIds = organizations.map((o: any) => o.id);
      const [outputsAgg, instancesAgg] = await Promise.all([
        supabase.rpc("get_org_ai_outputs_counts", { org_ids: orgIds }),
        supabase.rpc("get_org_ai_assistant_instances_counts", {
          org_ids: orgIds,
        }),
      ]);

      const outputsByOrg: Record<string, number> = {};
      if (!outputsAgg.error && Array.isArray(outputsAgg.data)) {
        for (const row of outputsAgg.data as any[]) {
          if (row.organization_id)
            outputsByOrg[row.organization_id] = Number(row.count) || 0;
        }
      }

      const instancesByOrg: Record<string, number> = {};
      if (!instancesAgg.error && Array.isArray(instancesAgg.data)) {
        for (const row of instancesAgg.data as any[]) {
          if (row.organization_id)
            instancesByOrg[row.organization_id] = Number(row.count) || 0;
        }
      }

      // Transform the data to match our interface, merging counts
      const transformedData: OrganizationMonitoringData[] = organizations.map(
        (org: any) => ({
          id: org.id,
          name: org.name,
          logo_url: org.logo_url,
          created_at: org.created_at,
          website: org.website,
          subscription_status: org.subscription_status,
          active_agents_count: org.active_agents_count,
          ai_assistant_instances_count:
            typeof instancesByOrg[org.id] === "number"
              ? instancesByOrg[org.id]
              : org.ai_assistant_instances_count,
          ai_outputs_count: outputsByOrg[org.id] ?? null,
          is_test_org: org.is_test_org ?? false,
          onboarding_meta: org.onboarding_meta,
          // Profile data now comes directly from the function
          profile_full_name:
            org.profile_full_name ||
            `${org.profile_first_name || ""} ${org.profile_last_name || ""}`.trim() ||
            null,
          profile_email: org.profile_email || null,
          profile_first_name: org.profile_first_name || null,
          profile_last_name: org.profile_last_name || null,
          // Industry data
          industry_name: org.industry_name || null,
          industry_emoji: org.industry_emoji || null,
          // Plan data
          plan_name: org.plan_name || null,
          plan_monthly_price: org.plan_monthly_price || null,
        }),
      );

      return { data: transformedData, error: null };
    } catch (error) {
      console.error("Service error:", error);
      return {
        data: null,
        error:
          error instanceof Error ? error : new Error("Unknown error occurred"),
      };
    }
  },

  // Helper function to get display name from profile data
  getDisplayName(org: OrganizationMonitoringData): string {
    if (org.profile_full_name) {
      return org.profile_full_name;
    }

    const firstName = org.profile_first_name || "";
    const lastName = org.profile_last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || "Unknown User";
  },

  // Helper function to get onboarding stage
  getOnboardingStage(onboardingMeta: any): string {
    if (!onboardingMeta) return "Not Started";

    if (onboardingMeta.completed === true) {
      return "Completed";
    }

    const currentStep = onboardingMeta.current_step || 1;
    const completedSteps = onboardingMeta.completed_steps || [];

    if (completedSteps.length > 0) {
      return "In Progress";
    }

    return "Not Started";
  },

  // Helper function to get subscription status display
  getSubscriptionStatusDisplay(status: string | null): {
    text: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className: string;
  } {
    const statusLower = (status || "free").toLowerCase();

    switch (statusLower) {
      case "active":
        return {
          text: "Active",
          variant: "default",
          className: "bg-green-100 text-green-800 border-green-200",
        };
      case "trialing":
        return {
          text: "Trial",
          variant: "secondary",
          className: "bg-blue-100 text-blue-800 border-blue-200",
        };
      case "past_due":
        return {
          text: "Past Due",
          variant: "destructive",
          className: "bg-red-100 text-red-800 border-red-200",
        };
      case "canceled":
        return {
          text: "Canceled",
          variant: "secondary",
          className: "bg-gray-100 text-gray-800 border-gray-200",
        };
      case "free":
      default:
        return {
          text: "Free",
          variant: "outline",
          className: "bg-gray-50 text-gray-700 border-gray-200",
        };
    }
  },

  async getDashboardMetrics(): Promise<{
    data: {
      new_signups_today: number;
      new_signups_yesterday: number;
      new_signups_last7: number;
      completed_today: number;
      completed_yesterday: number;
      completed_last7: number;
      outputs_today: number;
      outputs_yesterday: number;
      outputs_last7: number;
    } | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.rpc("get_super_admin_org_metrics");
      if (error) {
        return { data: null, error: new Error(error.message) };
      }
      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return { data: row as any, error: null };
    } catch (e) {
      return {
        data: null,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },
};
